import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, User, ChevronRight, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = ['All', 'Style', 'Stories', 'Impact', 'Behind the Scenes'];

const JournalPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchPosts();
  }, [selectedCategory, page]);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const params = { page, limit: 9 };
      if (selectedCategory !== 'All') {
        params.category = selectedCategory;
      }
      const response = await axios.get(`${API}/journal`, { params });
      setPosts(response.data.posts || []);
      setTotalPages(response.data.pages || 1);
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-32 pb-24" data-testid="journal-page">
      {/* Hero */}
      <section className="chaleur-container mb-16">
        <div className="max-w-3xl">
          <p className="caption text-primary mb-4">The Journal</p>
          <h1 className="text-4xl md:text-5xl font-light mb-6">
            Stories of Craft, Style & <span className="editorial-italic">Purpose</span>
          </h1>
          <p className="text-muted-foreground">
            Explore the world of luxury textiles—artisan stories, styling inspiration, 
            and the impact behind every thread.
          </p>
        </div>
      </section>

      {/* Categories & Search */}
      <section className="chaleur-container mb-12">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6">
          <div className="flex gap-4 overflow-x-auto no-scrollbar pb-2">
            {CATEGORIES.map(cat => (
              <button
                key={cat}
                onClick={() => { setSelectedCategory(cat); setPage(1); }}
                className={`whitespace-nowrap px-4 py-2 text-sm transition-colors ${
                  selectedCategory === cat
                    ? 'bg-primary text-primary-foreground'
                    : 'border border-border hover:border-primary'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input placeholder="Search articles..." className="pl-10 rounded-none" />
          </div>
        </div>
      </section>

      {/* Posts Grid */}
      <section className="chaleur-container">
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[...Array(6)].map((_, idx) => (
              <div key={idx} className="animate-pulse">
                <div className="aspect-[4/3] bg-secondary mb-4" />
                <div className="h-4 bg-secondary rounded w-1/4 mb-2" />
                <div className="h-6 bg-secondary rounded w-3/4 mb-2" />
                <div className="h-4 bg-secondary rounded" />
              </div>
            ))}
          </div>
        ) : posts.length > 0 ? (
          <>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((post, idx) => (
                <Link 
                  key={post.id}
                  to={`/journal/${post.slug}`}
                  className="group animate-fade-in-up"
                  style={{ animationDelay: `${idx * 0.05}s` }}
                >
                  <div className="aspect-[4/3] overflow-hidden mb-4">
                    <img 
                      src={post.featured_image || 'https://images.unsplash.com/photo-1606681246653-74521ac452f4?w=600&q=80'}
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <p className="caption text-primary mb-2">{post.category}</p>
                  <h3 className="text-xl font-light mb-2 group-hover:text-primary transition-colors">
                    {post.title}
                  </h3>
                  <p className="text-sm text-muted-foreground line-clamp-2">{post.excerpt}</p>
                  <div className="flex items-center gap-4 mt-4 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <User className="w-3 h-3" /> {post.author}
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" /> {new Date(post.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </Link>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center gap-2 mt-12">
                {[...Array(totalPages)].map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setPage(idx + 1)}
                    className={`w-10 h-10 flex items-center justify-center text-sm ${
                      page === idx + 1 
                        ? 'bg-primary text-primary-foreground' 
                        : 'border border-border hover:border-primary'
                    }`}
                  >
                    {idx + 1}
                  </button>
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-16">
            <p className="text-muted-foreground">No articles found in this category.</p>
          </div>
        )}
      </section>
    </div>
  );
};

export default JournalPage;
